import React, { useEffect, useMemo, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { createClerkSupabaseClient } from '../../../lib/supabase.js';
import { formatDateTime, formatUF } from '../../../styles/tokens/formatters';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import DashboardFilters from '../../../components/ui/fancy/DashboardFilters';
import { KpiGrid } from '../../../components/ui/KpiCard';
import GlobalSearch from '../../../components/ui/fancy/GlobalSearch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subDays } from 'date-fns';

const safeParseResumen = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw) || {}; } catch { return {}; }
};

// Componente para mostrar contrataciones en tabla simplificada (dentro de sesión expandida)
const SimpleContratacionRow = ({ contratacion }) => {
  const resumen = contratacion.resumen || {};
  const primaBase = resumen?.primaCalculada?.primabase || resumen?.total || {};
  const primaTitular = primaBase?.titular || 0;
  const primaCargas = Array.isArray(primaBase?.cargas) ? primaBase.cargas : [];
  
  const primaCarga = primaCargas.reduce((sum, c) => sum + (c.primaUF || 0), 0);
  const primaTotalContratacion = primaTitular + primaCarga;

  return (
    <TableRow className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/20 transition-colors">
      {/* Compañía */}
      <TableCell className="text-sm text-gray-900 dark:text-white py-3 px-4">
        {contratacion.compania_nombre || 'N/A'}
      </TableCell>

      {/* Producto / Plan */}
      <TableCell className="text-sm py-3 px-4">
        <div className="font-medium text-gray-900 dark:text-white">{contratacion.producto_nombre || 'N/A'}</div>
        <div className="text-xs text-gray-500 dark:text-gray-500">{contratacion.plan_nombre || 'N/A'}</div>
      </TableCell>

      {/* Prima Titular */}
      <TableCell className="text-sm text-center py-3 px-4">
        <div className="text-gray-900 dark:text-white">
          {formatUF(primaTitular, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </TableCell>

      {/* Prima Carga */}
      <TableCell className="text-sm text-center py-3 px-4">
        <div className={`${primaCarga > 0 ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>
          {formatUF(primaCarga, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </TableCell>

      {/* Prima Total */}
      <TableCell className="text-sm text-center py-3 px-4">
        <div className="font-semibold text-gray-900 dark:text-white">
          {formatUF(primaTotalContratacion, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      </TableCell>

      {/* Código */}
      <TableCell className="text-sm text-gray-600 dark:text-gray-400 py-3 px-4 font-mono text-xs">
        {contratacion.codigo || 'N/A'}
      </TableCell>
    </TableRow>
  );
};

// Componente para expandible detalle completo de contratación
const ContratacionRow = ({ contratacion, expanded, onToggle }) => {
  const resumen = contratacion.resumen || {};
  const primaBase = resumen?.primaCalculada?.primabase || resumen?.total || {};
  const primaTitular = primaBase?.titular || 0;
  const primaCargas = Array.isArray(primaBase?.cargas) ? primaBase.cargas : [];
  const cargas = Array.isArray(resumen?.cargasFamiliares) ? resumen.cargasFamiliares : [];

  const totalPrimasCargas = primaCargas.reduce((sum, c) => sum + (c.primaUF || 0), 0);
  const primaTotalContratacion = primaTitular + totalPrimasCargas;

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => onToggle(contratacion.id)}>
        <TableCell className="w-6">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>

        {/* Producto y Plan */}
        <TableCell className="text-sm">
          <div className="font-medium">{contratacion.producto_nombre || 'N/A'}</div>
          <div className="text-xs text-muted-foreground">{contratacion.plan_nombre || 'N/A'}</div>
          <div className="text-xs font-mono text-muted-foreground">{contratacion.codigo}</div>
        </TableCell>

        {/* Prima Titular */}
        <TableCell className="text-sm text-right">
          <div className="font-semibold text-blue-600 dark:text-blue-400">
            {formatUF(primaTitular)}
          </div>
        </TableCell>

        {/* Cargas */}
        <TableCell className="text-sm text-right">
          <div className="font-semibold">{cargas.length}</div>
          <div className="text-xs text-muted-foreground">familiares</div>
        </TableCell>

        {/* Prima Total */}
        <TableCell className="text-sm text-right">
          <div className="font-bold text-[var(--yool-success)] dark:text-green-400">
            {formatUF(primaTotalContratacion)}
          </div>
        </TableCell>

        {/* Estado */}
        <TableCell className="text-sm">
          <span className={`text-xs font-semibold ${
            contratacion.estado === 'activo'
              ? 'text-[var(--yool-success)]'
              : contratacion.estado === 'pendiente'
              ? 'text-yellow-600'
              : contratacion.estado === 'confirmado'
              ? 'text-[var(--yool-success)]'
              : 'text-[var(--yool-error)]'
          }`}>
            {contratacion.estado || 'N/A'}
          </span>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow className="cursor-default">
          <TableCell colSpan={6} className="px-0 py-0">
            <div className="bg-blue-50/30 dark:bg-blue-900/10 p-6">
              {/* Datos principales */}
              <div className="bg-white dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Cliente</span>
                    <div className="font-medium text-gray-900 dark:text-white">{contratacion.nombre || 'N/A'}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{contratacion.rut || 'N/A'}</div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Fecha</span>
                    <div className="text-sm text-gray-900 dark:text-white">{formatDateTime(contratacion.created_at)}</div>
                  </div>
                </div>

                {/* Resumen de precios */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Titular</span>
                    <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {formatUF(primaTitular)}
                    </div>
                  </div>
                  {cargas.length > 0 && (
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Cargas ({cargas.length})</span>
                      <div className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                        {formatUF(totalPrimasCargas)}
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold">Total Plan</span>
                    <div className="text-lg font-bold text-[var(--yool-success)] dark:text-green-400">
                      {formatUF(primaTotalContratacion)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalle de cargas en tabla */}
              {cargas.length > 0 && (
                <div className="bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase">Cargas Familiares</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Sexo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Edad</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Previsión</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Prima</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {cargas.map((carga, idx) => {
                          const primaCargaCorrespondiente = primaCargas[idx]?.primaUF || 0;

                          return (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="px-4 py-3 text-gray-900 dark:text-white capitalize">{carga.tipo || '—'}</td>
                              <td className="px-4 py-3 text-gray-900 dark:text-white">{carga.sexo || '—'}</td>
                              <td className="px-4 py-3 text-gray-900 dark:text-white">{carga.edad || '—'}</td>
                              <td className="px-4 py-3 text-gray-900 dark:text-white capitalize">{carga.prevision || '—'}</td>
                              <td className="px-4 py-3 text-right font-semibold text-blue-600 dark:text-blue-400">
                                {primaCargaCorrespondiente > 0 ? formatUF(primaCargaCorrespondiente) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const getEstado = (contratos) => {
  if (!contratos?.length) return null;
  const allConfirmed = contratos.every((c) => c.estado === 'confirmado');
  return allConfirmed ? 'confirmado' : 'pendiente';
};

const SessionRow = ({ sesion }) => {
  const [expanded, setExpanded] = useState(false);
  const contratos = sesion.contrataciones;
  const estado = getEstado(contratos);
  const sesionId = sesion.sesion_id;

  // Calcular prima total de la sesión
  const primaTotal = contratos.reduce((sum, contrato) => {
    const resumen = contrato.resumen || {};
    const primaBase = resumen?.primaCalculada?.primabase || resumen?.total || {};
    const primaTitular = primaBase?.titular || 0;
    const primaCargas = Array.isArray(primaBase?.cargas) ? primaBase.cargas : [];
    const totalCargas = primaCargas.reduce((cSum, c) => cSum + (c.primaUF || 0), 0);
    return sum + primaTitular + totalCargas;
  }, 0);

  // Total de cargas únicas en la sesión (de la primera contratación, que es la misma para todas)
  const firstResumen = contratos[0]?.resumen || {};
  const cargasFamiliares = Array.isArray(firstResumen?.cargasFamiliares) ? firstResumen.cargasFamiliares : [];
  const totalCargas = cargasFamiliares.length;

  return (
    <>
      <TableRow
        className="cursor-pointer border-b border-gray-200 dark:border-gray-700 transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-800/30"
        onClick={() => setExpanded((v) => !v)}
      >
        <TableCell className="w-6 text-gray-400">
          {expanded
            ? <ChevronDown className="h-4 w-4" />
            : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="whitespace-nowrap text-sm text-gray-700 dark:text-gray-300 py-4 px-4">
          {formatDateTime(sesion.fecha)}
        </TableCell>
        <TableCell className="text-sm py-4 px-4">
          <div className="font-medium text-gray-900 dark:text-white">{contratos[0]?.nombre || 'N/A'}</div>
          <div className="text-xs text-gray-500 dark:text-gray-500 font-mono">{contratos[0]?.rut || 'N/A'}</div>
        </TableCell>
        <TableCell className="text-sm py-4 px-4 text-center">
          <div className="inline-flex items-center justify-center rounded px-2.5 py-1 text-xs font-semibold bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
            {contratos.length}
          </div>
        </TableCell>
        <TableCell className="text-sm text-gray-700 dark:text-gray-300 py-4 px-4 text-center">
          <div className="font-medium">{totalCargas}</div>
          <div className="text-xs text-gray-500 dark:text-gray-500">carga{totalCargas !== 1 ? 's' : ''}</div>
        </TableCell>
        <TableCell className="text-sm text-center py-4 px-4">
          <div className="font-semibold text-gray-900 dark:text-white">{formatUF(primaTotal, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </TableCell>
        <TableCell className="text-sm text-center py-4 px-4">
          <span className={`text-xs font-medium ${
            estado === 'confirmado' 
              ? 'text-gray-700 dark:text-gray-300'
              : 'text-gray-500 dark:text-gray-500'
          }`}>
            {estado === 'confirmado' ? 'Confirmado' : 'Pendiente'}
          </span>
        </TableCell>
        <TableCell className="text-xs text-gray-500 dark:text-gray-500 font-mono py-4 px-4">
          {sesionId?.slice(0, 8)}…
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="cursor-default">
          <TableCell colSpan={8} className="bg-gray-50 dark:bg-gray-800/30 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative w-full overflow-auto">
              <Table className="text-sm">
                <TableHeader className="bg-gray-200 dark:bg-gray-700 border-y border-gray-300 dark:border-gray-600">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4">Compañía</TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4">Producto / Plan</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4">Prima Titular</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4">Prima Carga</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4">Prima Total</TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-10 px-4">Código</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contratos.map((contrato) => (
                    <SimpleContratacionRow
                      key={contrato.id}
                      contratacion={contrato}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ConversionesContrataciones = () => {
  const { session } = useSession();
  const supabase = useMemo(() => createClerkSupabaseClient(session), [session]);
  const navigate = useNavigate();

  // Enlace directo a una contratación por su código (`?search=CT-…`), p. ej. desde
  // la trazabilidad de una orden de pago. El listado se recorta a los últimos 30
  // días, así que con el parámetro se busca por código sobre toda la vista.
  const [searchParams] = useSearchParams();
  const codigoEnlazado = (searchParams.get('search') || '').trim();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState(codigoEnlazado);
  const [growthData, setGrowthData] = useState({ total: 0, lastPeriod: 0, percent: 0 });
  const [last30DaysCount, setLast30DaysCount] = useState(0);

  useEffect(() => {
    if (!session) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const from = filters.dateRange?.from ?? subDays(now, 30);
        const to = filters.dateRange?.to ?? now;

        const diff = to.getTime() - from.getTime();
        const prevFrom = new Date(from.getTime() - diff);
        const prevTo = new Date(from.getTime() - 1);

        const [currentRes, prevRes, listRes] = await Promise.all([
          supabase.from('company_contrataciones')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString()),
          supabase.from('company_contrataciones')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', prevFrom.toISOString())
            .lte('created_at', prevTo.toISOString()),
          // Se llegó desde un enlace a una contratación puntual: manda el código,
          // no el rango; si no, el listado normal de los últimos 30 días.
          (codigoEnlazado
            ? supabase.from('company_contrataciones')
                .select('id,created_at,nombre,rut,estado,codigo,producto_nombre,plan_nombre,cotizacion_id,datos_titular,compania_nombre')
                .eq('codigo', codigoEnlazado)
            : supabase.from('company_contrataciones')
                .select('id,created_at,nombre,rut,estado,codigo,producto_nombre,plan_nombre,cotizacion_id,datos_titular,compania_nombre')
                .gte('created_at', from.toISOString())
                .lte('created_at', to.toISOString())
          )
            .order('created_at', { ascending: false })
            .limit(500),
          // Query adicional para traer resumen de cotizaciones
          supabase.from('company_cotizaciones')
            .select('id,resumen')
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString()),
        ]);

        if (listRes.error) throw listRes.error;

        const currentCount = currentRes.count || 0;
        const prevCount = prevRes.count || 0;
        setLast30DaysCount(currentCount);
        setGrowthData({
          total: currentCount,
          lastPeriod: prevCount,
          percent: prevCount > 0 ? ((currentCount - prevCount) / prevCount) * 100 : 0,
        });

        // Obtener todos los cotizacion_ids únicos
        const cotizacionIds = [...new Set((listRes.data || []).map(r => r.cotizacion_id).filter(Boolean))];
        
        // Crear mapa de cotizacion_id -> sesion_id
        let sesionMap = {};
        let resumenMap = {};
        
        if (cotizacionIds.length > 0) {
          const { data: cotizaciones, error: cotizError } = await supabase.from('company_cotizaciones')
            .select('id,sesion_id,resumen')
            .in('id', cotizacionIds);
          
          if (cotizError) throw cotizError;
          
          (cotizaciones || []).forEach(cot => {
            sesionMap[cot.id] = cot.sesion_id;
            resumenMap[cot.id] = cot.resumen;
          });
        }

        // Agrupar contrataciones por sesion_id
        const bySession = {};
        for (const row of listRes.data || []) {
          const sesionId = sesionMap[row.cotizacion_id] || '__sin_sesion__';

          // Enriquecer contratación con resumen (ya viene compania_nombre en row)
          const resumenStr = resumenMap[row.cotizacion_id];
          
          const enrichedRow = {
            ...row,
            resumen: safeParseResumen(resumenStr)
          };

          if (!bySession[sesionId]) {
            bySession[sesionId] = {
              sesion_id: sesionId,
              fecha: row.created_at,
              contrataciones: []
            };
          }

          bySession[sesionId].contrataciones.push(enrichedRow);

          if (new Date(row.created_at) > new Date(bySession[sesionId].fecha)) {
            bySession[sesionId].fecha = row.created_at;
          }
        }

        setRows(Object.values(bySession).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
      } catch (e) {
        setError(e?.message || 'Error cargando datos');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [session, supabase, filters, codigoEnlazado]);

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((sesion) =>
      sesion.sesion_id?.toLowerCase().includes(q) ||
      sesion.contrataciones.some(
        (c) =>
          c.codigo?.toLowerCase().includes(q) ||
          c.producto_nombre?.toLowerCase().includes(q) ||
          c.plan_nombre?.toLowerCase().includes(q) ||
          c.nombre?.toLowerCase().includes(q) ||
          c.rut?.toLowerCase().includes(q)
      )
    );
  }, [rows, searchQuery]);

  const kpiItems = [
    {
      title: 'Contrataciones en rango',
      value: last30DaysCount,
      helper: 'Total de contrataciones',
      trend: 'neutral',
    },
    {
      title: 'Sesiones únicas',
      value: rows.length,
      helper: 'Sesiones con al menos 1 contratación',
      trend: 'neutral',
    },
    {
      title: 'Periodo anterior',
      value: growthData.lastPeriod,
      helper: 'Comparación equivalente',
      trend: growthData.percent >= 0 ? 'up' : 'down',
      delta: `${growthData.percent >= 0 ? '+' : ''}${Math.abs(growthData.percent).toFixed(1)}% vs periodo actual`,
    },
  ];

  return (
    <div className="space-y-8">
      <SectionHeader
        title="Conversiones - Contrataciones"
        subtitle="Agrupadas por sesión"
        action={<GlobalSearch onNavigate={(p) => navigate(p)} />}
      />

      <DashboardFilters
        onFilterChange={setFilters}
        showClientFilter={false}
        channels={[
          { value: 'web', label: 'Web Directa' },
          { value: 'fb', label: 'Facebook Ads' },
        ]}
      />

      <KpiGrid items={kpiItems} loading={loading} />

      <Card className="border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader className="flex flex-col md:flex-row items-center justify-between gap-4 border-b border-gray-200 dark:border-gray-700 pb-4">
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">Sesiones de Contratación</CardTitle>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar cliente, RUT o código..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 py-2 border border-gray-300 dark:border-gray-600 text-sm"
            />
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">Cargando sesiones...</div>
          ) : error ? (
            <div className="text-sm text-gray-700 dark:text-gray-300 py-8 text-center">{error}</div>
          ) : filteredRows.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">No hay sesiones para mostrar</div>
          ) : (
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-gray-800 border-y border-gray-300 dark:border-gray-700">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-6 text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4" />
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">Fecha</TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">Cliente</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">Contrataciones</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">Cargas</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">Prima Total</TableHead>
                    <TableHead className="text-center text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">Estado</TableHead>
                    <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-xs h-12 px-4">ID Sesión</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((sesion) => (
                    <SessionRow key={sesion.sesion_id} sesion={sesion} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversionesContrataciones;

